import React from 'react';
import { ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  getConditionalBackgroundStyle,
  getConditionalIconColor,
} from '@/pages/public/AdminLivePage';

interface IconButtonProps {
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  className?: string;
  label: string;
}

export default function IconButton({ icon, isActive, onClick, className, label }: IconButtonProps) {
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const iconColor = getConditionalIconColor({
    defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
    secondaryBackgroundColor: currentWorkspace?.visualIdentity?.secondaryBackgroundColor,
  });

  return (
    <div
      className={cn(
        'p-4 relative z-20 rounded-lg cursor-pointer w-full flex justify-center items-center h-full',
        isActive && 'rounded-b-none',
        className,
      )}
      style={getConditionalBackgroundStyle({
        theme: currentWorkspace?.visualIdentity?.theme || 'LIGHT',
        defaultIdentity: currentWorkspace?.visualIdentity?.defaultIdentity,
        defaultIdentityColor:
          currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
        isActive: isActive,
      })}
      onClick={onClick}
    >
      {isActive ? (
        <ChevronUp size={24} color={iconColor} />
      ) : React.isValidElement(icon) ? (
        <div className="flex flex-col justify-center items-center gap-0.5">
          {React.cloneElement(icon as React.ReactElement<any>, { color: iconColor })}
          <p
            className={`text-xs font-medium ${
              currentWorkspace?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
            }`}
          >
            {label}
          </p>
        </div>
      ) : (
        <div className="flex flex-col justify-center items-center gap-0.5">
          {icon}
          <p
            className={`text-xs font-medium ${
              currentWorkspace?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
            }`}
          >
            {label}
          </p>
        </div>
      )}
    </div>
  );
}
